import CryptoJS from "crypto-js";
import React, { memo, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { connect } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import DefaultView from "../components/DefaultView";
import PhoneNumberAccordion from "../components/PhoneNumberAccordion";
import {
  actionMe,
  actionDashboard,
  actionGetMyNumbers,
  actionFaxStats,
} from "../store/actions/authAction";
import { actionGetFaxQueuesCount } from "../store/actions/sentFaxesAction";
import { setAuthToken } from "../utils/helper";
import moment from "moment";
import {
  hasActiveSubscription,
  isSubscriptionCanceling,
  getPlanName,
  getPageAllowance,
  getPagesRemaining,
  getPagesUsed,
  getRenewalDate,
} from "../utils/subscription";

const Home = memo(
  ({
    actionMe,
    user,
    actionDashboard,
    dashboard,
    myNumbers,
    actionGetMyNumbers,
    getMyNumbersLoader,
    actionFaxStats,
    getFaxStatsLoader,
    faxStats,
    actionGetFaxQueuesCount,
    inFaxQueuesCount,
    outFaxQueuesCount,
  }) => {
    const [searchParams] = useSearchParams();
    const [faxStatFilter, setFaxStatFilter] = useState("1M");
    const userID = searchParams.get("user_ID");
    const token = searchParams.get("token");
    const {
      inboxTotalUnread,
      inboxTotalImportant,
    } = dashboard;
    const { totalReceivedFaxes, totalSentFaxes } = faxStats;

    const subscribed = hasActiveSubscription(user) || isSubscriptionCanceling(user);
    const canceling = isSubscriptionCanceling(user);
    const planName = getPlanName(user);
    const planAllowance = getPageAllowance(user);
    const pagesRemaining = getPagesRemaining(user);
    const pagesUsed = getPagesUsed(user);
    const renewalDate = getRenewalDate(user);

    const hasPageMeter =
      pagesRemaining != null && pagesUsed != null && planAllowance > 0;
    const pagesUsedPct = hasPageMeter
      ? Math.min(Math.round((pagesUsed / planAllowance) * 100), 100)
      : 0;
    const meterColor =
      pagesUsedPct >= 100
        ? "bg-danger"
        : pagesUsedPct >= 80
        ? "bg-warning"
        : "bg-success";

    if (userID && token) {
      // Encryption key and IV (Initialization Vector)
      const key = CryptoJS.enc.Utf8.parse("MuwdpPoXKQFigWhEO6TaUN5oFPoSm3DP");
      const iv = CryptoJS.enc.Utf8.parse("ttoxHoyErmLzguA1");

      // Decrypt the data using AES-128 decryption and CBC mode
      var decryptedUserIdData = CryptoJS.AES.decrypt(userID, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      // Convert the decrypted data to a string
      var descryptedUserId = decryptedUserIdData.toString(CryptoJS.enc.Utf8);

      // Decrypt the data using AES-128 decryption and CBC mode
      var decryptedTokenData = CryptoJS.AES.decrypt(token, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      // Convert the decrypted data to a string
      var descryptedToken = decryptedTokenData.toString(CryptoJS.enc.Utf8);

      
      descryptedToken && localStorage.setItem("authToken", descryptedToken);
      descryptedUserId && localStorage.setItem("authUserId", descryptedUserId);
      setAuthToken(descryptedToken);
      !user && actionMe();
    }

    // Initialize Bootstrap tooltips for elements using data-bs-toggle="tooltip"
    useEffect(() => {
      if (!window || !window.bootstrap || !window.bootstrap.Tooltip) return;
      const Tooltip = window.bootstrap.Tooltip;
      const triggers = Array.from(
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
      );
      const instances = triggers.map((el) => new Tooltip(el));
      return () => instances.forEach((inst) => inst.dispose());
    }, []);

    // Runs only when user changes
    useEffect(() => {
      if (user) {
        actionGetMyNumbers(user.user_ID);
        actionGetFaxQueuesCount(user.user_ID);
      }
    }, [user]);

    useEffect(() => {
      if (user && faxStatFilter) {
        actionFaxStats(faxStatFilter);
      }
    }, [user, faxStatFilter]);

    const hasData = totalReceivedFaxes || totalSentFaxes;
    const chartData = {
      options: {
        labels: hasData ? ["Faxes Received", "Faxes Sent"] : ["No Data"],
        tooltip: {
          enabled: hasData, // disable tooltip when no data
        },
        dataLabels: {
          enabled: hasData, // disable labels when no data
        },
        legend: {
          // show: hasData, // hide legend when no data
        },
      },
      series: hasData ? [totalReceivedFaxes || 0, totalSentFaxes || 0] : [1], // show single slice when no data
      colors: hasData
        ? ["#777aca", "#5156be"] // normal colors
        : ["#d3d3d3"],
      chart: {
        type: "pie",
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      hoverOffset: 4,
    };

    return (
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* start page title */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 main_heading">Dashboard</h4>
                  <div className="page-title-right">
                    <DefaultView />
                  </div>
                </div>
              </div>
            </div>
            {/* end page title */}
            <div className="row">
              <div className="col-xl-6 col-md-12">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h4 className="card-title">
                            Inbox
                            <i
                              className="fas fa-info-circle ms-2 text-info"
                              style={{ cursor: "pointer" }}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="View your inbox messages and important notifications"
                            ></i>
                          </h4>
                          <p className="card-title-desc">
                            Quickly view your important messages.
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* end card header */}
                    <div className="card-body">
                      <div className="d-flex flex-wrap gap-2">
                        <Link to="/inbox">
                          <button type="button" className="btn btn-success">
                            Messages{" "}
                            <span
                              className="badge bg-dark ms-1"
                              style={{ color: "green" }}
                            >
                              {inboxTotalUnread}
                            </span>
                          </button>
                        </Link>
                        <Link to="/inbox/important">
                          <button type="button" className="btn btn-danger">
                            Important{" "}
                            <span
                              className="badge bg-dark ms-1"
                              style={{ color: "green" }}
                            >
                              {inboxTotalImportant}
                            </span>
                          </button>
                        </Link>
                        <Link to="/compose-fax">
                          <button type="button" className="btn btn-primary">
                            Compose Fax
                          </button>
                        </Link>
                      </div>
                    </div>
                    {/* end card-body */}
                  </div>
                  {/* end card */}
                </div>
                <div className="col-xl-12">
                  <div className="card">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h4 className="card-title">
                            Fax Queues
                            <i
                              className="fas fa-info-circle ms-2 text-info"
                              style={{ cursor: "pointer" }}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Monitor your incoming and outgoing fax queues in real-time"
                            ></i>
                          </h4>
                          <p className="card-title-desc">
                            you can directly see faxes being sent or received in
                            queues.
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* end card header */}
                    <div className="card-body">
                      <div className="d-flex flex-wrap gap-2">
                        <Link to="/queues/IN">
                          <button type="button" className="btn btn-primary">
                            Incoming{" "}
                            {inFaxQueuesCount > 0 && (
                              <span
                                className="badge bg-dark ms-1"
                                style={{ color: "green" }}
                              >
                                {inFaxQueuesCount}
                              </span>
                            )}
                          </button>
                        </Link>
                        <Link to="/queues/OUT">
                          <button type="button" className="btn btn-success">
                            Outgoing{" "}
                            {outFaxQueuesCount > 0 && (
                              <span
                                className="badge bg-dark ms-1"
                                style={{ color: "green" }}
                              >
                                {outFaxQueuesCount}
                              </span>
                            )}
                          </button>
                        </Link>
                      </div>
                    </div>
                    {/* end card-body */}
                  </div>
                  {/* end card */}
                </div>
                <div className="col-xl-12">
                  <div className="card">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <Link to="/fax-number-subscriptions">
                            <h4 className="card-title">
                              Phone Numbers
                              <i
                                className="fas fa-info-circle ms-2 text-info"
                                style={{ cursor: "pointer" }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="Manage your phone number subscriptions and billing information"
                              ></i>
                            </h4>
                          </Link>
                          <p className="card-title-desc">
                            Your phone number subscription are listed below.
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* end card header */}
                    <div className="card-body">
                      <div className="accordion" id="accordionExample">
                        {getMyNumbersLoader && "Loading..."}
                        {myNumbers.map((data, index) => (
                          <PhoneNumberAccordion
                            key={index}
                            index={index}
                            phoneNumber={data.number}
                            subscriptionDate={moment(
                              data.number_activation_date
                            ).format("MMMM D, YYYY")}
                            nextBillingDate={moment(data.number_billing_date)
                              .add(1, "month")
                              .format("MMMM D, YYYY")}
                            status={data.number_status}
                          />
                        ))}
                        {myNumbers.length < 1 && !getMyNumbersLoader && (
                          <div className="alert alert-warning" role="alert">
                            <strong>Notice:</strong> You don't have any active
                            phone numbers under your account.
                          </div>
                        )}
                      </div>
                      {/* end accordion */}
                    </div>
                    {/* end card-body */}
                  </div>
                  {/* end card */}
                </div>
              </div>
              <div className="col-xl-6 col-md-12">
                <div className="row">
                  <div className="col-xl-12">
                    {/* card */}
                    <div className="card card-h-100">
                      {/* card body */}
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className="text-muted mb-3 lh-1 d-block text-truncate">
                            Current Subscription
                            <i
                              className="fas fa-info-circle ms-2 text-info"
                              style={{ cursor: "pointer" }}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Your active plan and page allowance"
                            ></i>
                          </span>
                        </div>
                        {subscribed ? (
                          <div className="text-center">
                            <h4 className="mb-1">
                              {planName} Plan
                              {canceling && (
                                <span className="badge bg-soft-warning text-warning ms-2 font-size-12">
                                  Canceling
                                </span>
                              )}
                            </h4>
                            {hasPageMeter ? (
                              <div className="mt-3 mb-3">
                                <div className="d-flex justify-content-between align-items-baseline mb-1">
                                  <span className="text-muted font-size-13">
                                    Pages used
                                  </span>
                                  <span className="font-size-13 fw-semibold">
                                    {pagesUsed.toLocaleString()} /{" "}
                                    {planAllowance.toLocaleString()}
                                  </span>
                                </div>
                                <div
                                  className="progress"
                                  style={{ height: "6px" }}
                                >
                                  <div
                                    className={`progress-bar ${meterColor}`}
                                    role="progressbar"
                                    style={{ width: `${pagesUsedPct}%` }}
                                    aria-valuenow={pagesUsed}
                                    aria-valuemin={0}
                                    aria-valuemax={planAllowance}
                                  ></div>
                                </div>
                                <p className="text-muted font-size-12 mb-0 mt-1">
                                  {pagesRemaining.toLocaleString()} pages left
                                  this period
                                </p>
                              </div>
                            ) : (
                              planAllowance > 0 && (
                                <p className="text-muted mb-3 mt-2">
                                  {planAllowance.toLocaleString()} pages / month
                                </p>
                              )
                            )}
                            <div className="d-flex justify-content-center align-items-center flex-wrap">
                              <span className="badge bg-soft-success text-success me-2">
                                Active
                              </span>
                              {renewalDate && (
                                <span className="font-size-13">
                                  {canceling ? "Ends" : "Renews"} on{" "}
                                  {renewalDate}
                                </span>
                              )}
                            </div>
                            <Link
                              to="/subscription"
                              className="btn btn-sm btn-outline-primary mt-3"
                            >
                              Manage Subscription
                            </Link>
                          </div>
                        ) : (
                          <div className="text-center">
                            <h4 className="mb-1">No active subscription</h4>
                            <p className="text-muted mb-3">
                              Subscribe to a plan to start sending faxes.
                            </p>
                            <Link
                              to="/subscription"
                              className="btn btn-primary"
                            >
                              Choose a plan
                            </Link>
                          </div>
                        )}
                      </div>
                      {/* end card body */}
                    </div>
                    {/* end card */}
                  </div>
                  {/* end col*/}
                  <div className="col-xl-12">
                    {/* card */}
                    <div className="card card-h-100">
                      {/* card body */}
                      <div className="card-body">
                        <div className="d-flex flex-wrap align-items-center mb-4">
                          <div className="d-flex align-items-center me-2">
                            <h5 className="card-title mb-0 me-2">
                              Fax Statistics
                              <i
                                className="fas fa-info-circle ms-2 text-info"
                                style={{ cursor: "pointer" }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title="View your fax activity statistics over different time periods"
                              ></i>
                            </h5>
                          </div>
                          <div className="ms-auto">
                            <div>
                              <button
                                type="button"
                                className={`btn btn-soft-secondary btn-sm ${
                                  faxStatFilter === "ALL" ? "active" : ""
                                }`}
                                style={{ marginRight: 5 }}
                                onClick={() => setFaxStatFilter("ALL")}
                              >
                                ALL
                              </button>
                              <button
                                type="button"
                                className={`btn btn-soft-primary btn-sm ${
                                  faxStatFilter === "1M" ? "active" : ""
                                }`}
                                style={{ marginRight: 5 }}
                                onClick={() => setFaxStatFilter("1M")}
                              >
                                1M
                              </button>
                              <button
                                type="button"
                                className={`btn btn-soft-secondary btn-sm ${
                                  faxStatFilter === "3M" ? "active" : ""
                                }`}
                                style={{ marginRight: 5 }}
                                onClick={() => setFaxStatFilter("3M")}
                              >
                                3M
                              </button>
                              <button
                                type="button"
                                className={`btn btn-soft-secondary btn-sm ${
                                  faxStatFilter === "6M" ? "active" : ""
                                }`}
                                style={{ marginRight: 5 }}
                                onClick={() => setFaxStatFilter("6M")}
                              >
                                6M
                              </button>
                              <button
                                type="button"
                                className={`btn btn-soft-secondary btn-sm ${
                                  faxStatFilter === "1Y" ? "active" : ""
                                }`}
                                style={{ marginRight: 5 }}
                                onClick={() => setFaxStatFilter("1Y")}
                              >
                                1Y
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-center">
                          <div className="col-sm">
                            <div
                              id="wallet-balance"
                              style={{ minHeight: "209.8px" }}
                            >
                              {getFaxStatsLoader ? (
                                <div
                                  style={{
                                    width: 400 * 0.7,
                                    height: 400 * 0.7,
                                  }}
                                  className="skeleton-chart-circle"
                                />
                              ) : (
                                <ReactApexChart
                                  options={chartData.options}
                                  series={chartData.series}
                                  type={chartData.chart.type}
                                  labels={chartData.labels}
                                  colors={chartData.colors}
                                  responsive={chartData.responsive}
                                  height={400}
                                  width={400}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* end card */}
                  </div>
                </div>
              </div>
            </div>
            {/* end row*/}
          </div>
          {/* container-fluid */}
        </div>
        {/* End Page-content */}
      </div>
    );
  }
);

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    dashboard: state.auth.dashboard,
    myNumbers: state.auth.myNumbers,
    getMyNumbersLoader: state.auth.getMyNumbersLoader,
    getFaxStatsLoader: state.auth.getFaxStatsLoader,
    faxStats: state.auth.faxStats,
    inFaxQueuesCount: state.sentFax.inFaxQueuesCount,
    outFaxQueuesCount: state.sentFax.outFaxQueuesCount,
  };
};
export default connect(mapStateToProps, {
  actionMe,
  actionDashboard,
  actionGetMyNumbers,
  actionFaxStats,
  actionGetFaxQueuesCount,
})(Home);
